import { ExternalLinkLine, InboxCustom, Trash } from '@archipro-design/icons';
import { ShareAltIcon } from '@fluentui/react-icons-northstar';
import { useBoolean } from 'ahooks';
import React from 'react';
import {
    BOARD_TILE_OVERLAY_CLASS,
    SHOW_COMMENT,
    SHOW_SHARE_PINS,
} from '~/modules/design-board/config';
import PinOverlayButton from './PinOverlayButton';
import * as S from './TileTopActionButtons.desktop.style';
import { useTracker } from '@archipro-website/tracker';
import type { DetailedPinWithChecked } from '../all-pins/AllPins';
import { useWebsiteReferralTracking } from '~/modules/tracking/util/websiteTracking';
import { getPinProfessional } from '../../util/get-pin-tile';

import cx from 'classnames';
import { useStyles } from '@archipro-design/aria';

const TileTopActionButtonsDesktop = ({
    professionalLink,
    onDelete,
    canDelete,
    canComment,
    pin,
}: {
    professionalLink?: string;
    onDelete?: () => void;
    canDelete?: boolean;
    canComment?: boolean;
    pin: DetailedPinWithChecked;
}) => {
    const [deleteTooltipState, deleteTooltipAction] = useBoolean();

    const [linkTooltipState, linkTooltipAction] = useBoolean();

    const [shareTooltipState, shareTooltipAction] = useBoolean();

    const [commentTooltipState, commentTooltipAction] = useBoolean();

    const { css } = useStyles();

    const tracker = useTracker();

    const pinProfessional = getPinProfessional(pin);

    const {
        referralTrackingURL,
        referralTrackingClass,
        referralTrackingParams,
    } = useWebsiteReferralTracking(professionalLink, {
        libraryItemID: pin.Target.ID,
        professionalTitle: pinProfessional?.Title || '',
    });

    const onClickDelete = (e: React.SyntheticEvent<HTMLElement>) => {
        e.stopPropagation();
        e.preventDefault();
        onDelete && onDelete();
    };

    const onClickComment = (e: React.SyntheticEvent<HTMLElement>) => {
        e.stopPropagation();
        e.preventDefault();
        //https://archipro.atlassian.net/browse/PLAT-4011
        console.log('TODO: PLAT-4011');
    };

    const onClickVisitWebsite = (e: React.SyntheticEvent<HTMLElement>) => {
        e.stopPropagation();

        tracker.log('PinMenuVisitWebsite', {
            label: pinProfessional?.Title || '',
            url: new URL(window.location.href),

            data: {
                LibraryItemID: pin.Target.ID,
                ProfessionalID: pinProfessional?.ID || 0,
            },
        });
    };

    const onClickShare = (e: React.SyntheticEvent<HTMLElement>) => {
        e.stopPropagation();
        e.preventDefault();
        //https://archipro.atlassian.net/browse/PLAT-3976
        console.log('TODO: PLAT-3976');
    };

    return (
        <div className={BOARD_TILE_OVERLAY_CLASS}>
            {SHOW_SHARE_PINS && (
                <PinOverlayButton
                    open={shareTooltipState}
                    className={cx(
                        css(S.ShareButton),
                        css(S.MessageToolTipStyle)
                    )}
                    booleanActions={shareTooltipAction}
                    onClick={onClickShare}
                    icon={<ShareAltIcon />}
                    toolTipText={'Share'}
                />
            )}

            {referralTrackingURL && (
                <PinOverlayButton
                    open={linkTooltipState}
                    className={cx(
                        css(S.LinkToButton),
                        css(S.MessageToolTipStyle),
                        referralTrackingClass
                    )}
                    booleanActions={linkTooltipAction}
                    onClick={onClickVisitWebsite}
                    link={referralTrackingURL}
                    icon={<ExternalLinkLine />}
                    toolTipText={`Visit company website`}
                    linkParams={referralTrackingParams}
                />
            )}

            {/* Reimplement comments when they are created */}
            {/* https://archipro.atlassian.net/browse/PLAT-4011 */}
            {SHOW_COMMENT && canComment && (
                <PinOverlayButton
                    className={cx(
                        css(S.CommentButton),
                        css(S.MessageToolTipStyle)
                    )}
                    open={commentTooltipState}
                    booleanActions={commentTooltipAction}
                    onClick={onClickComment}
                    icon={<InboxCustom />}
                    toolTipText={'Comment'}
                />
            )}

            {canDelete && (
                <PinOverlayButton
                    open={deleteTooltipState}
                    className={cx(
                        css(S.DeleteButton),
                        css(S.MessageToolTipStyle)
                    )}
                    booleanActions={deleteTooltipAction}
                    onClick={onClickDelete}
                    icon={<Trash />}
                    toolTipText={'Delete'}
                />
            )}
        </div>
    );
};

export default TileTopActionButtonsDesktop;
